import { useState } from "react";
import { Link2, Copy, AlertCircle, Plus, ExternalLink } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { KPICard, AdminModal, EmptyState } from "@/components/admin/shared";
import {
  useTrackingLinks,
  useCreateTrackingLink,
} from "@/hooks/admin/useMarketing";
import type { CreateTrackingLinkInput } from "@/lib/admin-api/marketing";

// ---------------------------------------------------------------------------
// Create tracking link modal
// ---------------------------------------------------------------------------

function CreateTrackingLinkModal({
  open,
  onClose,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
}) {
  const create = useCreateTrackingLink();
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [utmSource, setUtmSource] = useState("");
  const [utmMedium, setUtmMedium] = useState("");
  const [utmCampaign, setUtmCampaign] = useState("");

  const handleSubmit = () => {
    if (!name.trim()) { toast.error("Le nom est obligatoire."); return; }
    if (!url.trim()) { toast.error("L'URL est obligatoire."); return; }
    if (!utmSource.trim()) { toast.error("utm_source est obligatoire."); return; }
    if (!utmMedium.trim()) { toast.error("utm_medium est obligatoire."); return; }
    if (!utmCampaign.trim()) { toast.error("utm_campaign est obligatoire."); return; }

    const input: CreateTrackingLinkInput = {
      name,
      url,
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_campaign: utmCampaign,
    };
    create.mutate(input, {
      onSuccess: () => { toast.success("Lien de suivi créé."); onClose(); },
      onError: () => toast.error("Erreur lors de la création du lien."),
    });
  };

  // Build preview URL
  const previewUrl = url
    ? (() => {
        try {
          const u = new URL(url);
          if (utmSource) u.searchParams.set("utm_source", utmSource);
          if (utmMedium) u.searchParams.set("utm_medium", utmMedium);
          if (utmCampaign) u.searchParams.set("utm_campaign", utmCampaign);
          return u.toString();
        } catch {
          return "";
        }
      })()
    : "";

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title="Nouveau lien de suivi"
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={create.isPending}>
            {create.isPending ? "En cours..." : "Créer"}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2 space-y-1">
          <label className="text-sm font-medium text-stone-700">
            Nom <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="ex : Instagram Stories Juin 2026"
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
        </div>
        <div className="sm:col-span-2 space-y-1">
          <label className="text-sm font-medium text-stone-700">
            URL de destination <span className="text-red-500">*</span>
          </label>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://djayma.com/categories/laitage"
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            utm_source <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={utmSource}
            onChange={(e) => setUtmSource(e.target.value)}
            placeholder="ex : instagram"
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            utm_medium <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={utmMedium}
            onChange={(e) => setUtmMedium(e.target.value)}
            placeholder="ex : stories"
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
        </div>
        <div className="sm:col-span-2 space-y-1">
          <label className="text-sm font-medium text-stone-700">
            utm_campaign <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={utmCampaign}
            onChange={(e) => setUtmCampaign(e.target.value)}
            placeholder="ex : ramadan-2026"
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
        </div>
        {previewUrl && (
          <div className="sm:col-span-2 rounded-lg bg-stone-50 px-4 py-3 space-y-1">
            <p className="text-xs font-medium text-stone-500">Aperçu de l'URL générée</p>
            <p className="text-xs font-mono text-stone-700 break-all">{previewUrl}</p>
          </div>
        )}
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function TrackingLinks() {
  const [modalOpen, setModalOpen] = useState(false);
  const { data, isLoading, isError } = useTrackingLinks();
  const links = data?.links ?? [];

  const totalClicks = links.reduce((acc, l) => acc + l.clicks, 0);
  const totalConversions = links.reduce((acc, l) => acc + l.conversions, 0);
  const totalRevenue = links.reduce((acc, l) => acc + l.revenue_fcfa, 0);

  const buildFullUrl = (link: typeof links[number]): string => {
    try {
      const u = new URL(link.url);
      u.searchParams.set("utm_source", link.utm_source);
      u.searchParams.set("utm_medium", link.utm_medium);
      u.searchParams.set("utm_campaign", link.utm_campaign);
      return u.toString();
    } catch {
      return link.url;
    }
  };

  const handleCopy = (link: typeof links[number]) => {
    const fullUrl = buildFullUrl(link);
    navigator.clipboard.writeText(fullUrl).then(() =>
      toast.success("URL copiée dans le presse-papiers."),
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <Link2 className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Liens de suivi
              </h1>
              <p className="text-sm text-stone-500">
                Suivi des campagnes marketing via UTM
              </p>
            </div>
          </div>
          <Button onClick={() => setModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Nouveau lien
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <KPICard label="Liens actifs" value={links.length} format="number" />
          <KPICard label="Clics total" value={totalClicks} format="number" />
          <KPICard label="Conversions" value={totalConversions} format="number" />
          <KPICard label="CA généré" value={totalRevenue} format="currency" />
        </div>

        {/* Table */}
        {isLoading && (
          <div className="space-y-3 animate-pulse">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 rounded bg-stone-100" />
            ))}
          </div>
        )}

        {isError && (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            Impossible de charger les liens de suivi.
          </div>
        )}

        {!isLoading && !isError && links.length === 0 && (
          <EmptyState
            icon={<Link2 className="h-6 w-6" />}
            title="Aucun lien de suivi"
            description="Créez des liens UTM pour mesurer l'efficacité de vos campagnes."
            action={{ label: "Nouveau lien", onClick: () => setModalOpen(true) }}
          />
        )}

        {!isLoading && !isError && links.length > 0 && (
          <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-100 bg-stone-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Nom</th>
                    <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Source</th>
                    <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Medium</th>
                    <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Campaign</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Clics</th>
                    <th className="hidden sm:table-cell px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Conv.</th>
                    <th className="hidden sm:table-cell px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">CA (FCFA)</th>
                    <th className="hidden xl:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Créé le</th>
                    <th className="w-20 px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {links.map((l) => (
                    <tr key={l.id} className="hover:bg-stone-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-stone-900">
                        {l.name}
                      </td>
                      <td className="hidden md:table-cell px-4 py-3 font-mono text-xs text-stone-500">
                        {l.utm_source}
                      </td>
                      <td className="hidden md:table-cell px-4 py-3 font-mono text-xs text-stone-500">
                        {l.utm_medium}
                      </td>
                      <td className="hidden lg:table-cell px-4 py-3 font-mono text-xs text-stone-500">
                        {l.utm_campaign}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-stone-700 font-semibold">
                        {(l.clicks ?? 0).toLocaleString("fr-SN")}
                      </td>
                      <td className="hidden sm:table-cell px-4 py-3 text-right tabular-nums text-stone-600">
                        {(l.conversions ?? 0).toLocaleString("fr-SN")}
                      </td>
                      <td className="hidden sm:table-cell px-4 py-3 text-right tabular-nums text-stone-600">
                        {(l.revenue_fcfa ?? 0).toLocaleString("fr-SN")}
                      </td>
                      <td className="hidden xl:table-cell px-4 py-3 text-stone-500">
                        {new Date(l.created_at).toLocaleDateString("fr-SN")}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            title="Copier l'URL"
                            onClick={() => handleCopy(l)}
                            className="rounded-md p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                          <a
                            href={buildFullUrl(l)}
                            target="_blank"
                            rel="noreferrer"
                            className="rounded-md p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
                            title="Ouvrir le lien"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <CreateTrackingLinkModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </AdminLayout>
  );
}
